import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/utils';
import { Package, Calendar, MapPin, Map, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { findStadium } from '../../data/stadiums';
import StadiumPlanModal from '../tickets/StadiumPlanModal';

const StockValue = ({ value, onClick, clickable }) => {
  const num = parseInt(value) || 0;
  return (
    <span 
      onClick={onClick}
      className={cn(
        'font-mono font-bold text-lg',
        num > 0 && 'text-emerald-600 dark:text-emerald-400',
        num < 0 && 'text-red-600 dark:text-red-400',
        num === 0 && 'text-zinc-500',
        clickable && num !== 0 && 'cursor-pointer hover:scale-110 transition-transform'
      )}
    >
      {num > 0 ? `+${num}` : num}
    </span>
  );
};

// Extract match number from event name (M2, M48, M99, etc.)
const extractMatchNumber = (eventName) => {
  if (!eventName) return 9999;
  const match = eventName.match(/\b[M]?0*(\d{1,3})\b/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  return 9999;
};

// Extract phase name from event (Quarterfinal #4, Men's Final, etc.) - FOR ROLAND GARROS
const extractPhaseName = (eventName) => {
  if (!eventName) return null;
  
  // Common Roland Garros phases - ORDER MATTERS! Check more specific patterns first
  const phasePatterns = [
    // Semifinals with number - most specific
    /Men's Semifinal #\d/i,
    /Women's Semifinal #?\d?/i,
    /Semifinal\s*#\d/i,
    // Quarterfinals
    /Quarterfinal\s*#?\d/i,
    /Quarterfinals/i,
    /Quart\s*de\s*finale/i,
    // Semifinals generic
    /Semifinals/i,
    /Demi-finale/i,
    // Finals - Women's BEFORE Men's to avoid "WOMAN" matching "MAN"
    /Women's Final/i,
    /Men's Final/i,
    /Finale/i,
    // Rounds
    /1st Round/i,
    /2nd Round/i,
    /3rd Round/i,
    /4th Round/i,
    /Round\s*\d/i,
    /1er Tour/i,
    /2e Tour/i,
    /3e Tour/i,
    /4e Tour/i,
  ];
  
  for (const pattern of phasePatterns) {
    const match = eventName.match(pattern);
    if (match) {
      return match[0];
    }
  }
  
  // Try to extract "Day X - [Phase]" pattern
  const dayPhaseMatch = eventName.match(/Day\s*\d+\s*[-–—]\s*([^-–—]+)/i);
  if (dayPhaseMatch && dayPhaseMatch[1]) {
    const phase = dayPhaseMatch[1].trim();
    // Only return if it looks like a phase name
    if (phase.length > 3 && !phase.toLowerCase().includes('session') && !phase.toLowerCase().includes('chatrier') && !phase.toLowerCase().includes('lenglen')) {
      return phase;
    }
  }
  
  return null;
};

// Format event name - extract just the team names and phase
// "M2 KOREA VS TBD - FIFA WORLD CUP 2026 - GROUP A" -> "KOREA VS TBD - GROUP A"
const formatEventNameForDisplay = (eventName, matchNum) => {
  if (!eventName) return '';
  
  // Clean up the event name
  let cleaned = eventName.replace(/\s+/g, ' ').trim();
  
  // Remove the match number prefix if present (M2, M48, etc.)
  cleaned = cleaned.replace(/^M\d+\s*[-–—]?\s*/i, '');
  
  // Remove "FIFA WORLD CUP 2026" or similar
  cleaned = cleaned.replace(/\bFIFA\s*WORLD\s*CUP\s*2026?\b/gi, '').trim();
  cleaned = cleaned.replace(/\bWORLD\s*CUP\s*2026?\b/gi, '').trim();
  cleaned = cleaned.replace(/\bWC\s*2026?\b/gi, '').trim();
  
  // Clean up multiple dashes and extra spaces
  cleaned = cleaned.replace(/\s*[-–—]+\s*/g, ' - ').trim();
  cleaned = cleaned.replace(/^\s*[-–—]+\s*/, '').trim();
  cleaned = cleaned.replace(/\s*[-–—]+\s*$/, '').trim();
  cleaned = cleaned.replace(/\s+/g, ' ');
  
  return cleaned;
};

export const DeltaView = ({ data = [], onCategoryClick }) => {
  // Stadium plan modal state
  const [stadiumPlanOpen, setStadiumPlanOpen] = useState(false);
  const [selectedStadium, setSelectedStadium] = useState(null);
  const [selectedMatchInfo, setSelectedMatchInfo] = useState(null);
  
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);

  const handleCategoryClick = (event, category, stock) => {
    if (stock === 0 || !onCategoryClick) return;
    
    // Determine if we should show ACHAT (+) or VENTE (-)
    const viewType = stock > 0 ? 'achat' : 'vente';
    const matchNum = extractMatchNumber(event.evenement);
    
    onCategoryClick({
      viewType,
      eventName: event.evenement,
      dateEvenement: event.date_evenement,
      matchNum: matchNum === 9999 ? null : matchNum,
      category,
      stock
    });
  };
  
  const handleShowStadiumPlan = (stadium, event, e) => {
    e.stopPropagation();
    setSelectedStadium(stadium);
    
    // Extract match number from event name
    const matchNum = extractMatchNumber(event.evenement);
    const matchNumStr = matchNum !== 9999 ? `M${matchNum}` : null;
    
    setSelectedMatchInfo({
      matchNum: matchNumStr,
      eventName: event.evenement,
      date: event.date_evenement,
      lieu: event.lieu,
      ville: event.ville
    });
    setStadiumPlanOpen(true);
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Package className="h-12 w-12 mb-4 opacity-50" />
        <p>Aucun stock à afficher</p>
        <p className="text-sm">Importez des billets pour voir le delta</p>
      </div>
    );
  }

  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {sortedData.map((event, index) => {
        const matchNum = extractMatchNumber(event.evenement);
        const stadium = findStadium(event.lieu, event.ville);
        // Extract phase name for Roland Garros (Quarterfinal #4, Men's Final, etc.)
        const phaseName = extractPhaseName(event.evenement);
        // Check if this is Roland Garros (has session or court like Chatrier/Lenglen)
        const isRolandGarros = event.session || 
          (event.lieu && (event.lieu.includes('Chatrier') || event.lieu.includes('Lenglen') || event.lieu.includes('Outside')));
        
        return (
          <div 
            key={`delta-${index}`}
            data-testid={`delta-card-${index}`}
            className="delta-card"
          >
            <div className="pb-2 pt-3 px-3">
              {matchNum < 9999 && (
                <div className="mb-1">
                  {/* M11 + Phase Name in GOLD for Roland Garros */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bg-[#C4A052] text-black text-sm font-bold px-2 py-1 rounded inline-block w-fit">
                      M{matchNum}
                    </span>
                    {/* Show phase name in GOLD next to match number - ROLAND GARROS ONLY */}
                    {isRolandGarros && phaseName && (
                      <span className="text-[#C4A052] text-sm font-bold">
                        {phaseName}
                      </span>
                    )}
                  </div>
                  
                  {/* Session badge + Court for Roland Garros */}
                  {isRolandGarros && (
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {event.session && (
                        <span className={cn(
                          "text-xs font-medium px-2 py-0.5 rounded",
                          event.session.toLowerCase().includes('evening') || event.session.toLowerCase().includes('night')
                            ? "bg-purple-600/30 text-purple-300"
                            : "bg-yellow-600/30 text-yellow-300"
                        )}>
                          {event.session.toLowerCase().includes('evening') || event.session.toLowerCase().includes('night') ? '🌙 Evening' : '☀️ Day'}
                        </span>
                      )}
                      {event.lieu && (
                        <span className="text-xs text-zinc-400">
                          {event.lieu.includes('Chatrier') ? '🏟️ Chatrier' : 
                           event.lieu.includes('Lenglen') ? '🏟️ Lenglen' : 
                           event.lieu.includes('Outside') ? '🌳 Outside' : event.lieu}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Full match name for non-RG events */}
                  {!isRolandGarros && event.evenement && (
                    <p className="text-xs font-medium text-white mt-1.5 leading-tight">
                      {formatEventNameForDisplay(event.evenement, matchNum)}
                    </p>
                  )}
                </div>
              )}
              {matchNum >= 9999 && event.evenement && (
                <p className="text-xs font-medium text-white mb-1">
                  {event.evenement}
                </p>
              )}
              <div className="flex flex-wrap gap-2 text-xs text-zinc-400 mt-1">
                {event.date_evenement && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {event.date_evenement}
                  </span>
                )}
                {event.ville && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {event.ville}
                  </span>
                )}
              </div>
              
              {/* Stadium Info */}
              {stadium && (
                <div className="mt-2 pt-2 border-t border-zinc-700">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#C4A052] truncate">{stadium.name}</p>
                      <p className="text-xs text-zinc-500 flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {stadium.capacity.toLocaleString()} places
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => handleShowStadiumPlan(stadium, event, e)}
                      className="h-6 px-2 text-xs border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10 ml-2 shrink-0"
                    >
                      <Map className="h-3 w-3 mr-1" />
                      Plan
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="pt-1 px-3 pb-3">
              <div className="space-y-1">
                {event.stocks && Object.keys(event.stocks).length > 0 ? (
                  Object.entries(event.stocks)
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([category, stock]) => (
                      <div 
                        key={category}
                        data-testid={`delta-category-${matchNum}-${category}`}
                        onClick={() => handleCategoryClick(event, category, stock)}
                        className={cn(
                          "flex items-center justify-between py-1 px-2 rounded bg-zinc-800",
                          stock !== 0 && onCategoryClick && "cursor-pointer hover:bg-zinc-700 transition-colors"
                        )}
                      >
                        <span className="text-xs text-zinc-300">
                          {category}
                        </span>
                        <StockValue 
                          value={stock} 
                          clickable={!!onCategoryClick && stock !== 0}
                        />
                      </div>
                    ))
                ) : (
                  <p className="text-sm text-zinc-500 text-center py-2">
                    Pas de stock
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Stadium Plan Modal */}
      <StadiumPlanModal
        open={stadiumPlanOpen}
        onOpenChange={setStadiumPlanOpen}
        stadium={selectedStadium}
        matchInfo={selectedMatchInfo}
      />
    </div>
  );
};

export default DeltaView;
